import React from "react";
import { useLocation as useUserLocation } from "@/lib/useLocation";
import WeatherWidget from "@/components/WeatherWidget";
import HardinessZone from "@/components/HardinessZone";
import AISageTip from "@/components/AISageTip";
import TaskList from "@/components/TaskList";
import { Link } from "react-router-dom";
import { ArrowRight, Sprout } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Dashboard() {
  const { location } = useUserLocation();

  return (
    <div className="space-y-10" data-testid="dashboard">
      {/* Title */}
      <div className="max-w-3xl">
        <div className="label-overline">Volume I — The Handbook</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter mt-2">
          Tend by the moon,<br />
          <span className="text-primary italic">harvest by the sun.</span>
        </h1>
        <p className="text-foreground/70 mt-4 max-w-xl leading-relaxed">
          The Garden Sage reads your local skies and your living roster of plants — then whispers
          the small, right things to do today.
        </p>
      </div>

      {/* Top bento */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <AISageTip scope="daily" location={location} />
        </div>
        <div className="space-y-6">
          <WeatherWidget location={location} />
          <HardinessZone location={location} />
        </div>
      </div>

      {/* Tasks + planner CTA */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <TaskList scope="daily" title="Today's to-dos" />
        </div>
        <div className="sage-card p-6 flex flex-col justify-between" data-testid="planner-cta">
          <div>
            <div className="label-overline">Plan further</div>
            <h3 className="font-serif text-2xl mt-1 font-light">Look forward through the season</h3>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              Hop into the Planner to organize tasks by week or month, with sage guidance for each stretch of time.
            </p>
          </div>
          <Link to="/planner">
            <Button variant="outline" className="mt-4 gap-2 border-primary/40 text-primary hover:bg-primary/10" data-testid="open-planner-cta">
              Open planner <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Garden empty teaser */}
      <div className="sage-card p-8 flex flex-col md:flex-row items-start gap-6">
        <Sprout className="w-10 h-10 text-accent" strokeWidth={1.2} />
        <div className="flex-1">
          <div className="label-overline">Your garden</div>
          <h3 className="font-serif text-2xl font-light mt-1">Tell the Sage what you're growing</h3>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Browse the seeded library or add your own. The more the Sage knows, the more pointed the counsel.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/library">
            <Button variant="outline" data-testid="goto-library-btn">Plant library</Button>
          </Link>
          <Link to="/garden">
            <Button className="bg-primary hover:bg-primary/90" data-testid="goto-garden-btn">My garden</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
